import { useEffect, useState } from 'react';

import { SITE_URL } from '../env.ts';
import { platformStore } from '../platform/config.ts';
import { LEGACY_ORIGINS, MOVE_NOTE_KEY, type MoveNote, moveNote } from '../pwa/move.ts';
import type { KeyValueStore } from '../storage/kv.ts';
import { LimeTick } from './icons.tsx';

interface Props {
	/** Heard when the line appears (Today keeps other notices off the page while it's up). */
	onShow?: () => void;
	/** Tests. */
	store?: KeyValueStore;
}

/**
 * Today's line after the move from the old address (pwa/move.ts): it came across, or it
 * didn't and the old address can try again (it still has the data). Shown until dismissed.
 * The same quiet line as the challenge feedback.
 */
export function MoveNotice({ onShow, store = platformStore }: Props) {
	const [note, setNote] = useState<MoveNote | null>(() => moveNote(store));
	useEffect(() => {
		if (note) onShow?.();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);
	if (!note) return null;
	const host = new URL(SITE_URL).host;
	const retry = LEGACY_ORIGINS[0];
	return (
		<div className="fb" role="status" data-testid="move-notice">
			{note === 'ok' && <LimeTick className="fb-tick" />}
			<p className="fb-text">
				{note === 'ok' ? (
					<>
						<b>Sparrow has moved to {host}.</b> Your streak came with you.
					</>
				) : (
					<>
						<b>Your streak didn’t come across.</b>{' '}
						{retry ? (
							<a className="fb-link" href={`${retry}/`}>
								Try again
							</a>
						) : (
							'It’s still saved at the old address.'
						)}
					</>
				)}
			</p>
			<button
				type="button"
				className="fb-x"
				aria-label="Dismiss"
				onClick={() => {
					store.remove(MOVE_NOTE_KEY);
					setNote(null);
				}}
			>
				<svg viewBox="0 0 24 24" aria-hidden="true">
					<path d="M6 6l12 12M18 6 6 18" />
				</svg>
			</button>
		</div>
	);
}
